import axios from "axios";
import { io, Socket } from "socket.io-client";

import { baseUrl } from "../store/slices/auth";

export type ChatAuthor = {
  _id: string;
  username: string;
  email?: string;
  role?: string;
};

export type ChatAttachment = {
  url: string;
  name: string;
  mimeType?: string;
  size?: number;
};

export type ChatMessage = {
  _id: string;
  cohortId: string;
  recipientId?: ChatAuthor | string | null;
  authorId: ChatAuthor | string;
  text: string;
  attachments: ChatAttachment[];
  replyToId?: ChatMessage | string | null;
  editedAt?: string | null;
  deletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ChatContext = {
  cohortId: string;
  recipientId?: string | null;
};

export type ChatMember = ChatAuthor & {
  telephone?: string;
  isAdmin?: boolean;
  cohortId?: string | null;
};

export type ChatConversation = {
  partner: ChatMember;
  lastMessage: {
    _id: string;
    authorId: string;
    recipientId: string;
    text: string;
    attachments: ChatAttachment[];
    deletedAt?: string | null;
    createdAt: string;
  };
};

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const chatApi = {
  listMessages: async (
    context: ChatContext,
    before?: string,
    limit = 30
  ) => {
    const response = await axios.get<{
      messages: ChatMessage[];
      hasMore: boolean;
      nextBefore?: string | null;
    }>(`${baseUrl}chat/messages`, {
      headers: authHeaders(),
      params: {
        cohortId: context.cohortId,
        recipientId: context.recipientId || undefined,
        before,
        limit,
      },
    });
    return response.data;
  },

  listMembers: async (cohortId: string) => {
    const response = await axios.get<{ members: ChatMember[] }>(
      `${baseUrl}chat/members`,
      {
        headers: authHeaders(),
        params: { cohortId },
      }
    );
    return response.data.members;
  },

  listConversations: async (cohortId: string) => {
    const response = await axios.get<{
      conversations: ChatConversation[];
    }>(`${baseUrl}chat/conversations`, {
      headers: authHeaders(),
      params: { cohortId },
    });
    return response.data.conversations;
  },

  sendMessage: async (
    context: ChatContext,
    payload: {
      text: string;
      attachments?: ChatAttachment[];
      replyToId?: string;
    }
  ) => {
    const response = await axios.post<{ message: ChatMessage }>(
      `${baseUrl}chat/messages`,
      { ...context, ...payload },
      { headers: authHeaders() }
    );
    return response.data.message;
  },

  uploadAttachments: async (context: ChatContext, files: File[]) => {
    const formData = new FormData();
    formData.append("cohortId", context.cohortId);
    if (context.recipientId) {
      formData.append("recipientId", context.recipientId);
    }
    files.forEach((file) => formData.append("files", file));

    const response = await axios.post<{ attachments: ChatAttachment[] }>(
      `${baseUrl}chat/attachments`,
      formData,
      { headers: authHeaders() }
    );
    return response.data.attachments;
  },

  editMessage: async (id: string, text: string) => {
    const response = await axios.patch<{ message: ChatMessage }>(
      `${baseUrl}chat/messages/${id}`,
      { text },
      { headers: authHeaders() }
    );
    return response.data.message;
  },

  deleteMessage: async (id: string) => {
    const response = await axios.delete<{ message: ChatMessage }>(
      `${baseUrl}chat/messages/${id}`,
      { headers: authHeaders() }
    );
    return response.data.message;
  },
};

export const createChatSocket = (): Socket =>
  io(baseUrl, {
    auth: { token: localStorage.getItem("token") || "" },
    path: "/socket.io",
    transports: ["polling", "websocket"],
    upgrade: true,
    reconnection: true,
    reconnectionAttempts: 5,
    timeout: 10_000,
  });
